import React from "react";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  Edit,
  Trash2,
  Eye,
  Plus,
} from "lucide-react";

// Toast Configuration
export const useToast = () => {
  const defaultOptions = {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
  };

  return {
    success: (message, options = {}) =>
      toast.success(message, { ...defaultOptions, ...options }),
    error: (message, options = {}) =>
      toast.error(message, { ...defaultOptions, ...options }),
    info: (message, options = {}) =>
      toast.info(message, { ...defaultOptions, ...options }),
    warning: (message, options = {}) =>
      toast.warning(message, { ...defaultOptions, ...options }),
    custom: (message, options = {}) =>
      toast(message, { ...defaultOptions, ...options }),
    dismiss: (toastId) => toast.dismiss(toastId),
    dismissAll: () => toast.dismiss(),
  };
};

export const ToastProvider = ({ children }) => {
  return (
    <>
      {children}
      <ToastContainer
        toastClassName="relative flex p-4 min-h-10 rounded-xl justify-between overflow-hidden cursor-pointer bg-white shadow-lg border border-gray-100"
        progressClassName="Toastify__progress-bar Toastify__progress-bar--animated bg-primary"
      />
    </>
  );
};

const PaginationControls = ({
  currentPage,
  totalPages,
  onNext,
  onPrev,
  onPageChange,
}) => {
  return (
    <div className="flex items-center justify-between mt-6">
      <div className="text-sm text-gray-500">
        Page {currentPage} of {totalPages}
      </div>
      <div className="flex items-center gap-1.5">
        <Button
          variant="secondary"
          onClick={onPrev}
          disabled={currentPage === 1}
          className="p-2 hover:bg-primary/5"
        >
          <ChevronLeft size={18} className="text-primary" />
        </Button>

        {[...Array(totalPages)].map((_, index) => {
          const pageNum = index + 1;
          if (
            pageNum === 1 ||
            pageNum === totalPages ||
            (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
          ) {
            return (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? "primary" : "text"}
                className={`w-9 h-9 ${
                  currentPage === pageNum ? "font-semibold" : "text-gray-600"
                }`}
                onClick={() => onPageChange(pageNum)}
              >
                {pageNum}
              </Button>
            );
          }
          return null;
        })}

        <Button
          variant="secondary"
          onClick={onNext}
          disabled={currentPage === totalPages}
          className="p-2 hover:bg-primary/5"
        >
          <ChevronRight size={18} className="text-primary" />
        </Button>
      </div>
    </div>
  );
};

/* const Button = ({
  children,
  variant = "primary",
  onClick,
  className = "",
  disabled = false,
  icon: Icon,
  loading = false,
}) => {
  const baseClasses =
    "px-4 py-2 rounded-xl transition-all duration-200 font-medium focus:outline-none flex items-center gap-2 focus:ring-2 focus:ring-primary/30";

  const variantClasses = {
    primary:
      "bg-primary text-white hover:bg-primary-dark shadow-sm hover:shadow-md",
    secondary:
      "bg-white text-primary-dark border border-gray-200 hover:border-primary/40 hover:bg-primary/5",
    text: "bg-transparent text-gray-600 hover:text-primary hover:bg-primary/5",
    danger:
      "bg-red-500/90 text-white hover:bg-red-600 shadow-sm hover:shadow-md",
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
      ) : (
        <>
          {Icon && React.createElement(Icon, { className: "w-5 h-5" })}
          {children}
        </>
      )}
    </button>
  );
}; */
const Button = ({
  children,
  variant = "primary",
  onClick,
  className = "",
  disabled = false,
  icon: Icon,
  loading = false,
  type = "button",
  form,
}) => {
  const baseClasses =
    "px-4 py-2 rounded-xl transition-all duration-200 font-medium focus:outline-none flex items-center justify-center gap-2 text-center focus:ring-2 focus:ring-primary/30";

  const variantClasses = {
    primary:
      "bg-primary text-white hover:bg-primary-dark shadow-sm hover:shadow-md",
    deep: "bg-deep text-white hover:bg-deep-light shadow-sm hover:shadow-md",
    secondary:
      "bg-white text-primary-dark border border-gray-200 hover:border-primary/40 hover:bg-primary/5",
    text: "bg-transparent text-gray-600 hover:text-primary hover:bg-primary/5",
    danger:
      "bg-red-500/90 text-white hover:bg-red-600 shadow-sm hover:shadow-md",
  };

  // Disabled state styling
  const disabledClasses = disabled || loading 
    ? "opacity-50 cursor-not-allowed hover:transform-none" 
    : "";

  return (
    <button
      type={type}
      form={form}
      className={`${baseClasses} ${variantClasses[variant]} ${disabledClasses} ${className}`}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
      ) : (
        <>
          {Icon && React.createElement(Icon, { className: "w-5 h-5" })}
          {children}
        </>
      )}
    </button>
  );
};

const Card = ({
  title,
  children,
  accentColor = "primary",
  className = "",
  headerAction,
  footer,
  ...props
}) => {
  const colorMap = {
    primary: "bg-white border border-gray-100",
    dark: "bg-gray-50 border border-gray-200",
  };

  return (
    <div
      className={`p-5 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 ${colorMap[accentColor]} ${className}`}
      {...props}
    >
      {(title || headerAction) && (
        <div className="flex justify-between items-center mb-4 pb-3">
          {title && (
            <h3 className="text-lg font-semibold text-primary-dark">{title}</h3>
          )}
          {headerAction && <div>{headerAction}</div>}
        </div>
      )}
      {children}
      {footer && (
        <div className="mt-4 pt-3 border-t border-gray-100">{footer}</div>
      )}
    </div>
  );
};

const Badge = ({ children, variant = "default", className = "" }) => {
  const variantClasses = {
    default: "bg-gray-100 text-gray-800",
    primary: "bg-primary/10 text-primary",
    success: "bg-green-500/10 text-green-700",
    warning: "bg-yellow-500/10 text-yellow-700",
    danger: "bg-red-500/10 text-red-700",
  };

  return (
    <span
      className={`inline-flex items-center text-xs px-3 py-1 rounded-full font-medium ${variantClasses[variant]} ${className}`}
    >
      {children}
    </span>
  );
};

const FilterSearchBar = ({
  searchTerm,
  onSearchChange,
  filterOptions,
  onFilterChange,
}) => {
  return (
    <Card className="mb-6" accentColor="dark">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search..."
            className="w-full pl-10 pr-4 py-2.5 bg-white rounded-lg text-sm border border-gray-200 focus:ring-2 focus:ring-primary/30 focus:border-transparent"
            value={searchTerm}
            onChange={onSearchChange}
          />
          <Search size={18} className="absolute left-3 top-3 text-gray-400" />
        </div>
        <select
          className="w-full sm:w-48 py-2.5 px-4 bg-white rounded-lg text-sm border border-gray-200 focus:ring-2 focus:ring-primary/30 focus:border-transparent"
          onChange={onFilterChange}
        >
          {filterOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </Card>
  );
};

const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onDelete,
  itemToDelete,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-scale-in">
        <div className="p-5 border-b border-gray-100">
          <h3 className="text-lg font-semibold">Confirm Delete</h3>
        </div>
        <div className="p-5">
          <p className="text-gray-600">
            Delete{" "}
            <span className="text-primary-dark font-medium">
              "{itemToDelete?.name}"
            </span>{" "}
            permanently?
          </p>
        </div>
        <div className="p-5 border-t border-gray-100 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onDelete}>
            Confirm Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

const LoadingSpinner = ({ message = "Loading..." }) => {
  return (
    <div className="flex justify-center items-center min-h-screen">
      <div className="animate-spin inline-block w-6 h-6 border-2 border-t-transparent border-primary rounded-full mr-2"></div>
      <span className="text-gray-600">{message}</span>
    </div>
  );
};

const ErrorDisplay = ({ error }) => {
  return (
    <div className="bg-red-50/80 p-4 rounded-xl border border-red-100">
      <p className="text-red-700 text-sm">{error}</p>
    </div>
  );
};

/* const EmptyState = ({ message, action }) => {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-gray-500 space-y-3 min-h-[50vh]">
      <p>{message}</p>
      {action && (
        <Button variant="primary" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
};
 */
const EmptyState = ({
  icon: Icon,
  message,
  buttonLabel,
  buttonIcon: ButtonIcon,
  buttonLink,
  buttonOnClick, // 👈 tambahin ini
}) => {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12">
      {Icon && (
        <div className="h-24 w-24 text-gray-400 mb-4 flex items-center justify-center">
          <Icon className="h-12 w-12" />
        </div>
      )}
      <p className="text-gray-600 mb-4">{message}</p>
      {buttonLabel &&
        (buttonLink ? (
          <Link to={buttonLink}>
            <Button icon={ButtonIcon}>{buttonLabel}</Button>
          </Link>
        ) : (
          <Button icon={ButtonIcon} onClick={buttonOnClick}>
            {buttonLabel}
          </Button>
        ))}
    </div>
  );
};

const MobileCard = ({ title, description, badge, actions }) => {
  return (
    <Card className="mb-4">
      <h4 className="font-medium text-base mb-2 text-primary-dark">{title}</h4>
      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{description}</p>
      {badge && (
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge variant={badge.variant}>{badge.text}</Badge>
        </div>
      )}
      <div className="flex justify-end space-x-2">{actions}</div>
    </Card>
  );
};

const TableHeader = ({ columns }) => {
  return (
    <thead>
      <tr className="border-b-2 border-gray-100">
        {columns.map((column, index) => (
          <th
            key={index}
            className="p-3 text-left text-sm font-semibold text-primary-dark"
          >
            {column}
          </th>
        ))}
      </tr>
    </thead>
  );
};

const TableRow = ({ data, actions }) => {
  return (
    <tr className="hover:bg-gray-50 transition-colors">
      {Object.values(data).map((value, index) => (
        <td key={index} className="p-3 text-sm text-gray-700">
          {value}
        </td>
      ))}
      <td className="p-3">
        <div className="flex justify-end space-x-2">{actions}</div>
      </td>
    </tr>
  );
};

const ExportButton = ({ onExport }) => {
  return (
    <Button variant="secondary" onClick={onExport}>
      Export to Excel
    </Button>
  );
};

const DateFilter = ({ selectedDate, onDateChange }) => {
  return (
    <div className="mb-4 md:mb-0">
      <label className="text-sm font-medium mr-3 text-primary-dark">
        Select Date:
      </label>
      <input
        type="date"
        value={selectedDate}
        onChange={onDateChange}
        className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/30"
      />
    </div>
  );
};

const ResetFilterButton = ({ onReset }) => {
  return (
    <Button variant="text" onClick={onReset}>
      Reset Filters
    </Button>
  );
};

const AddButton = ({ onClick, label }) => {
  return (
    <Button
      variant="primary"
      onClick={onClick}
      className="flex items-center gap-1.5"
    >
      <Plus size={16} />
      <span>{label}</span>
    </Button>
  );
};

const EditDeleteButtons = ({ onEdit, onDelete }) => {
  return (
    <div className="flex space-x-2">
      <Button
        variant="text"
        className="text-gray-600 hover:text-primary"
        onClick={onEdit}
      >
        <Edit size={16} />
      </Button>
      <Button
        variant="text"
        className="text-gray-600 hover:text-red-600"
        onClick={onDelete}
      >
        <Trash2 size={16} />
      </Button>
    </div>
  );
};

const ViewDetailsButton = ({ onView }) => {
  return (
    <Button
      variant="text"
      className="text-primary hover:text-primary-dark inline-flex items-center"
      onClick={onView}
    >
      <Eye size={16} className="mr-1.5" />
      View Details
    </Button>
  );
};

const Breadcrumb = ({ items, className = "" }) => {
  return (
    <nav className={`flex ${className}`} aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        {items.map((item, index) => (
          <li key={index} className="flex items-center">
            {index !== items.length - 1 ? (
              <>
                <Link
                  to={item.path}
                  className="text-sm text-gray-500 hover:text-primary transition-colors"
                >
                  {item.label}
                </Link>
                <ChevronRight size={14} className="mx-1 text-gray-400" />
              </>
            ) : (
              <span className="text-sm text-primary-dark font-medium">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

const Modal = ({ isOpen, onClose, title, children, actions }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-scale-in">
        <div className="p-5 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-primary-dark">{title}</h3>
        </div>
        <div className="p-5">{children}</div>
        <div className="p-5 border-t border-gray-100 flex justify-end gap-3">
          {actions}
        </div>
      </div>
    </div>
  );
};

const SkeletonLoader = ({ className = "" }) => {
  return (
    <div className={`animate-pulse ${className}`}>
      <div className="bg-gray-100 h-6 rounded-xl mb-4 w-3/4"></div>
      <div className="bg-gray-100 h-4 rounded-xl mb-4 w-1/2"></div>
      <div className="bg-gray-100 h-4 rounded-xl w-2/3"></div>
    </div>
  );
};

const StatCard = ({
  title,
  value,
  icon: Icon,
  change,
  changeType = "neutral",
  className = "",
}) => {
  const changeColors = {
    positive: "text-green-600",
    negative: "text-red-600",
    neutral: "text-gray-600",
  };

  return (
    <Card className={`p-4 ${className}`}>
      <div className="flex justify-between items-start">
        <div>
          <h4 className="text-sm font-medium text-gray-500 mb-2">{title}</h4>
          <p className="text-2xl font-bold text-primary-dark">{value}</p>
          {change && (
            <div className={`mt-2 text-sm ${changeColors[changeType]}`}>
              {change}
            </div>
          )}
        </div>
        {Icon && (
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon className="w-6 h-6 text-primary" />
          </div>
        )}
      </div>
    </Card>
  );
};

const ActivityItem = ({ title, description, time, badge }) => {
  return (
    <div className="p-3 flex justify-between items-center hover:bg-gray-50 rounded-xl transition-colors">
      <div>
        <p className="font-medium text-primary-dark">{title}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        {badge && <Badge variant={badge.variant}>{badge.text}</Badge>}
        <span className="text-xs text-gray-400">{time}</span>
      </div>
    </div>
  );
};

const SectionHeader = ({ title, subtitle, action, className = "" }) => {
  return (
    <div className={`flex justify-between items-center mb-6 ${className}`}>
      <div>
        <h2 className="text-xl font-bold text-primary-dark">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
};

const PageHeader = ({ title, subtitle, actions, className = "" }) => {
  return (
    <div className={`mb-8 ${className}`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-primary-dark">{title}</h1>
          {subtitle && <p className="text-gray-500 mt-1">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-3">{actions}</div>}
      </div>
    </div>
  );
};

const AdminLayout = ({ children, className = "", header, sidebar }) => {
  return (
    <div className={`p-6 min-h-screen bg-gray-50 ${className}`}>
      {header && <div className="mb-8">{header}</div>}
      <div className="flex gap-6">
        {sidebar && <div className="w-64">{sidebar}</div>}
        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
};

const Grid = ({
  children,
  cols = { default: 1, md: 2, lg: 3 },
  gap = 6,
  className = "",
}) => {
  return (
    <div
      className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-${gap} ${className}`}
    >
      {children}
    </div>
  );
};

const NotificationList = ({ notifications = [], className = "" }) => {
  return (
    <ul className={`space-y-2 ${className}`}>
      {notifications.map((notification, index) => (
        <li
          key={index}
          className="p-3 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
        >
          {notification}
        </li>
      ))}
    </ul>
  );
};

const Tab = ({
  tabs,
  activeTab,
  onTabChange,
  className = "",
  variant = "default", // "default" or "compact"
}) => {
  const baseClasses = "flex space-x-1 bg-gray-100 p-1 rounded-lg";
  const compactClasses = "flex space-x-1 bg-gray-50 p-0.5 rounded-md";

  const tabClasses = variant === "compact" ? compactClasses : baseClasses;

  return (
    <div className={`${tabClasses} ${className}`}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onTabChange(tab.id)}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === tab.id
              ? "bg-white text-primary-dark shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          {tab.icon && React.createElement(tab.icon, { className: "h-4 w-4" })}
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export {
  PaginationControls,
  FilterSearchBar,
  DeleteConfirmationModal,
  LoadingSpinner,
  ErrorDisplay,
  EmptyState,
  MobileCard,
  TableHeader,
  TableRow,
  ExportButton,
  DateFilter,
  ResetFilterButton,
  AddButton,
  EditDeleteButtons,
  ViewDetailsButton,
  Breadcrumb,
  Modal,
  SkeletonLoader,
  Card,
  Button,
  StatCard,
  Badge,
  ActivityItem,
  SectionHeader,
  PageHeader,
  AdminLayout,
  Grid,
  NotificationList,
  Tab,
};
